import React from 'react';
import { cn } from '@/lib/utils';
import { useDashboardItems } from '@/hooks/useDashboards';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useAuth } from '@/hooks/useAuth';
import { replaceDashboardVariables } from '@/utils/dashboardVariableReplacer';
import { renderTextWithBold } from '@/utils/dashboardTextFormat';
import { DASHBOARD_COMPONENTS } from '@/config/dashboardComponents';
import { ReportSectionHeader } from '@/components/custom-reports/ReportSectionHeader';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Skeleton } from '@/components/ui/skeleton';

interface CanvasItem {
  id: string;
  componentKey: string;
  label: string;
  displayName?: string | null;
  category: string;
  route: string;
  layout: string;
  order: number;
  height?: number;
  width?: number;
  x?: number;
  y?: number;
}

function getItemDefaultTitle(item: CanvasItem): string {
  if (item.componentKey.startsWith('content:')) {
    if (item.componentKey === 'content:header') return 'Header';
    if (item.componentKey === 'content:subheader') return 'Subheader';
    if (item.componentKey === 'content:text') return 'Text';
    return 'Table of contents';
  }
  const config = getComponentConfig(item.componentKey);
  return config?.label ?? item.label;
}

function layoutToSpan(layout: string): number {
  const m = layout.match(/^span(\d)$/);
  if (m) return Math.min(9, Math.max(1, parseInt(m[1], 10)));
  if (layout === 'col1' || layout === 'col2' || layout === 'col3') return 3;
  if (layout === 'full' || layout === 'grow') return 9;
  return 3;
}

const SPAN_TO_GRID_CLASS: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
};

function layoutToGridClasses(layout: string): string {
  return SPAN_TO_GRID_CLASS[layoutToSpan(layout)] ?? 'md:col-span-3';
}

function getComponentConfig(key: string) {
  return DASHBOARD_COMPONENTS.find((c) => c.key === key) ?? null;
}

function getItemTitle(item: CanvasItem): string {
  return item.displayName ?? getItemDefaultTitle(item);
}

function TocBlock({ canvasItems, excludeId }: { canvasItems: CanvasItem[]; excludeId?: string }) {
  const entries = canvasItems.filter(
    (i) =>
      i.id !== excludeId &&
      i.componentKey !== 'content:toc' &&
      (i.label?.trim() || i.componentKey)
  );
  if (entries.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        Add headers, components, or content to generate TOC
      </p>
    );
  }
  return (
    <nav className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground mb-2">Contents</p>
      <ul className="space-y-1 text-sm">
        {entries.map((entry) => {
          const isHeader = entry.componentKey === 'content:header';
          const isSubheader = entry.componentKey === 'content:subheader';
          const displayLabel = entry.label?.trim() || entry.componentKey;
          return (
            <li key={entry.id}>
              <span
                className="block text-primary truncate cursor-default"
                title={displayLabel}
              >
                {displayLabel}
              </span>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ContentBlockPreview({
  componentKey,
  text,
  canvasItems,
  excludeId,
}: {
  componentKey: string;
  text: string;
  canvasItems?: CanvasItem[];
  excludeId?: string;
}) {
  if (componentKey === 'content:header') {
    return <ReportSectionHeader level={1} text={text || 'Header'} />;
  }
  if (componentKey === 'content:subheader') {
    return <ReportSectionHeader level={2} text={text || 'Subheader'} />;
  }
  if (componentKey === 'content:text') {
    return (
      <p className="text-sm text-muted-foreground">{renderTextWithBold(text || 'Enter your text here...')}</p>
    );
  }
  if (componentKey === 'content:toc') {
    return <TocBlock canvasItems={canvasItems ?? []} excludeId={excludeId} />;
  }
  return null;
}

function DashboardItemRenderer({
  item,
  canvasItems,
  compact,
}: {
  item: CanvasItem;
  canvasItems: CanvasItem[];
  compact?: boolean;
}) {
  const { user } = useAuth();
  const { currentBrand } = useBrandManager();
  const componentKey = item.componentKey ?? '';

  if (!componentKey) {
    return null;
  }

  if (componentKey.startsWith('content:')) {
    const text = replaceDashboardVariables(item.label ?? '', user);
    return (
      <ContentBlockPreview
        componentKey={componentKey}
        text={text}
        canvasItems={canvasItems}
        excludeId={componentKey === 'content:toc' ? item.id : undefined}
      />
    );
  }

  const config = getComponentConfig(componentKey);
  const Component = config?.component ?? null;

  if (!Component) {
    return (
      <div className="text-xs text-muted-foreground italic py-2">
        Component not in registry
      </div>
    );
  }

  const props: Record<string, unknown> = {};
  if (config?.requiresBrandId && currentBrand?.id) {
    props.brandId = currentBrand.id;
  }
  if (compact) {
    props.compact = true;
  }

  return (
    <ErrorBoundary
      fallback={({ error }) => (
        <div className="text-xs text-muted-foreground italic py-2">
          Widget unavailable — {error?.message ?? 'unexpected error'}
        </div>
      )}
    >
      <React.Suspense fallback={<Skeleton className="h-20 w-full" />}>
        <Component {...props} />
      </React.Suspense>
    </ErrorBoundary>
  );
}

export interface DashboardOverlayContentProps {
  dashboardId: string | null | undefined;
  /** When set, items from this dashboard are appended after primary dashboard items (merge user default with global). */
  mergeWithDashboardId?: string | null;
  emptyMessage?: string;
  className?: string;
  /** Use compact layout (smaller padding, tighter spacing) for overlay panels */
  compact?: boolean;
  /** Add border to widget cards for prominence on overlay background */
  bordered?: boolean;
  /** Use glassmorphism style (translucent, blurred) for widget cards */
  glass?: boolean;
}

export function DashboardOverlayContent({
  dashboardId,
  mergeWithDashboardId,
  emptyMessage = 'No widgets configured. Add components in the Dashboard Builder.',
  className,
  compact = false,
  bordered = false,
  glass = false,
}: DashboardOverlayContentProps) {
  const { items: primaryItems, isLoading: primaryLoading } = useDashboardItems(dashboardId ?? undefined);
  const { items: secondaryItems, isLoading: secondaryLoading } = useDashboardItems(
    mergeWithDashboardId ?? undefined
  );

  const isLoading = primaryLoading || (!!mergeWithDashboardId && secondaryLoading);

  const mergedItems = React.useMemo(() => {
    const primary = primaryItems ?? [];
    const secondary = secondaryItems ?? [];
    if (secondary.length === 0) return primary;
    const maxOrder = primary.length > 0 ? Math.max(...primary.map((i) => i.order), 0) : 0;
    const offset = maxOrder + 1;
    return [
      ...primary,
      ...secondary.map((i) => ({ ...i, order: i.order + offset })),
    ].sort((a, b) => a.order - b.order);
  }, [primaryItems, secondaryItems]);

  const canvasItems: CanvasItem[] = mergedItems.map((d) => ({
    id: d.id,
    componentKey: d.component_key ?? '',
    label: d.label ?? '',
    displayName: d.display_name ?? undefined,
    category: d.category,
    route: d.route,
    layout: d.layout,
    order: d.order,
    width: d.width ?? undefined,
    height: d.height ?? undefined,
    x: d.x ?? undefined,
    y: d.y ?? undefined,
  }));

  if (isLoading) {
    return (
      <div className={className}>
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (canvasItems.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">{emptyMessage}</p>
    );
  }

  return (
    <div className={className}>
      <div
        className={cn(
          'grid grid-cols-1 md:grid-cols-9',
          compact ? 'gap-2' : 'gap-4'
        )}
      >
        {canvasItems.map((item) => (
          <div
            key={item.id}
            id={`canvas-item-${item.id}`}
            className={cn(
              'flex flex-col min-h-0',
              layoutToGridClasses(item.layout),
              compact ? 'p-2 rounded-lg' : 'p-4 rounded-xl',
              glass
                ? 'bg-white/20 backdrop-blur-md'
                : 'bg-background',
              bordered && 'border border-border'
            )}
          >
            <p className="text-xs font-medium text-muted-foreground truncate shrink-0 mb-1">
              {getItemTitle(item)}
            </p>
            <div className="min-h-0 flex-1">
              <DashboardItemRenderer item={item} canvasItems={canvasItems} compact={compact} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
